import { createSelector } from "reselect";

/**
 * Helpers: platform filtering
 */
export const getAllowedPlatforms = (settingObj: any): Set<string> => {
  const allowed = new Set<string>();
  if (!settingObj) return allowed;
  if (settingObj.facebook) allowed.add("facebook");
  if (settingObj.instagram) allowed.add("instagram");
  if (settingObj.twitter) allowed.add("twitter");
  if (settingObj.linkedin) allowed.add("linkedin");
  if (settingObj.threads) allowed.add("threads");
  return allowed;
};

export const getAllowedPlatformsFromImmutable = (aiSetting: any): Set<string> => {
  const allowed = new Set<string>();
  if (!aiSetting) return allowed;
  const getVal = (k: string) => (aiSetting?.get ? aiSetting.get(k) : aiSetting?.[k]);
  if (getVal("facebook")) allowed.add("facebook");
  if (getVal("instagram")) allowed.add("instagram");
  if (getVal("twitter")) allowed.add("twitter");
  if (getVal("linkedin")) allowed.add("linkedin");
  if (getVal("threads")) allowed.add("threads");
  return allowed;
};

export const isPostOnAllowedPlatform = (post: any, allowed: Set<string>): boolean => {
  // If no selection => do not filter
  if (!allowed || allowed.size === 0) return true;
  const platform = (post?.data?.platform?.value || post?.platform)?.toString().toLowerCase();
  if (!platform) return true;
  return allowed.has(platform);
};

/**
 * Selector to get project from state
 */
export const selectProject = (state: any) => state.projectReducer?.get("project");

/**
 * Selector to get project loading state
 */
export const selectProjectLoading = (state: any) => state.projectReducer?.get("loading");

/**
 * Selector to get aiSetting from project
 */
export const selectAiSetting = (state: any) => {
  const project = selectProject(state);
  return project?.get("aiSetting");
};

/**
 * Convert immutable object to plain object
 */
const toPlainObject = (immutableObj: any): any => {
  if (!immutableObj) return null;
  return immutableObj?.toJS ? immutableObj.toJS() : immutableObj;
};

/**
 * Selector to get aiSetting as plain object
 */
export const selectAiSettingPlain = createSelector([selectAiSetting], (aiSetting: any) => {
  if (!aiSetting) return null;
  return aiSetting?.toJS ? aiSetting.toJS() : aiSetting;
});

/**
 * Selector to validate if aiSetting is valid
 * Valid when at least one of the required fields exists: companyIndustry, customerLocation, serviceDescription
 */
export const selectHasValidAiSetting = (state: any) => {
  const project = selectProject(state);

  // If no project or aiSetting is undefined, return true
  if (!project) return true;

  const aiSetting = selectAiSetting(state);
  if (aiSetting === undefined) return true;

  // If aiSetting is null or undefined, return false
  if (!aiSetting) return false;

  const settingObj = toPlainObject(aiSetting);
  const hasCompanyIndustry = !!settingObj.companyIndustry;
  const hasCustomerLocation = !!settingObj.customerLocation;
  const hasServiceDescription = !!settingObj.serviceDescription;

  return settingObj && (hasCompanyIndustry || hasCustomerLocation || hasServiceDescription);
};

/**
 * Selector to validate if aiSetting has all required fields
 * (For use cases that require full validation)
 */
export const selectHasFullAiSetting = (state: any) => {
  const aiSetting = selectAiSetting(state);

  if (!aiSetting) return false;

  const settingObj = toPlainObject(aiSetting);

  return !!(
    settingObj.serviceDescription &&
    settingObj.customerLocation &&
    settingObj.companyIndustry &&
    settingObj.language
  );
};

/**
 * Selector to validate if topic exists
 */
export const selectHasValidTopic = (state: any) => {
  const project = selectProject(state);

  if (!project) return true;

  const aiSetting = selectAiSetting(state);
  if (aiSetting === undefined) return true;
  if (!aiSetting) return false;

  const settingObj = toPlainObject(aiSetting);
  return !!settingObj.topic;
};

/**
 * Selector to get topic from aiSetting
 */
export const selectTopic = (state: any) => {
  const settingObj = selectAiSettingPlain(state);
  return settingObj?.topic || "";
};

/**
 * Selector to get project information as plain object
 */
export const selectProjectPlain = (state: any) => {
  const project = selectProject(state);
  return toPlainObject(project);
};

/**
 * Selector to get detailed information from aiSetting
 */
export const selectAiSettingDetails = (state: any) => {
  const settingObj = selectAiSettingPlain(state);

  return {
    topic: settingObj?.topic || "",
    customPrompt: settingObj?.customPrompt || "",
    language: settingObj?.language || "vi",
    totalDays: settingObj?.totalDays || 30,
    includeWeekends: settingObj?.includeWeekends || false,
    startDate: settingObj?.startDate || "",
    endDate: settingObj?.endDate || "",
    companyIndustry: settingObj?.companyIndustry || "",
    customerLocation: settingObj?.customerLocation || "",
    serviceDescription: settingObj?.serviceDescription || "",
  };
};

/**
 * Selector to get social posts from state
 */
export const selectSocialPosts = createSelector(
  [(state: any) => state.aiContent?.get?.("socialPosts"), selectAiSettingPlain],
  (socialPostsImmutableOrArray: any, settingObj: any) => {
    if (!socialPostsImmutableOrArray) return [] as any[];

    const postsArray = socialPostsImmutableOrArray.toJS
      ? socialPostsImmutableOrArray.toJS()
      : socialPostsImmutableOrArray;

    const allowedPlatforms = new Set<string>();
    if (settingObj?.facebook) allowedPlatforms.add("facebook");
    if (settingObj?.instagram) allowedPlatforms.add("instagram");
    if (settingObj?.twitter) allowedPlatforms.add("twitter");
    if (settingObj?.linkedin) allowedPlatforms.add("linkedin");
    if (settingObj?.threads) allowedPlatforms.add("threads");

    if (allowedPlatforms.size === 0) {
      return postsArray;
    }

    const filtered = postsArray.filter((post: any) => {
      const platform = (post?.data?.platform?.value || post?.platform)?.toString().toLowerCase();
      return allowedPlatforms.has(platform);
    });

    return filtered;
  }
);

/**
 * Selector to check if there are posts that can be approved
 * Returns true if there are posts that are not yet approved
 */
export const selectHasPostsToApprove = (state: any) => {
  const socialPosts = selectSocialPosts(state);

  if (!socialPosts || socialPosts.length === 0) {
    return false;
  }

  // Check if there are any posts with status other than "Approved"
  return socialPosts.some((post: any) => {
    const status = post?.data?.status?.value || post?.status?.value || "Unscheduled";
    return status !== "Approved";
  });
};

/**
 * Selector to get time settings from aiSetting
 * Returns default time if not set
 */
export const selectTimeSettings = createSelector([selectAiSettingPlain], (settingObj: any) => {
  const defaultTime = {
    hour: 9,
    minute: 30,
    ampm: "AM" as const,
    timezone: "Asia/Ho_Chi_Minh",
    includeWeekends: true,
  };

  if (!settingObj) {
    return defaultTime;
  }

  const startTime = settingObj.startTime || settingObj.scheduledTime;

  if (startTime && typeof startTime === "string") {
    const [timeStr] = startTime.split(" ");
    const [hours, minutes] = timeStr.split(":").map(Number);

    if (!isNaN(hours) && !isNaN(minutes)) {
      const hour24 = hours;
      const hour12 = hour24 === 0 ? 12 : hour24 > 12 ? hour24 - 12 : hour24;
      const ampm = hour24 >= 12 ? "PM" : "AM";

      return {
        hour: hour12,
        minute: minutes,
        ampm,
        timezone: settingObj.timezone || defaultTime.timezone,
        includeWeekends:
          settingObj.includeWeekends !== undefined
            ? settingObj.includeWeekends
            : defaultTime.includeWeekends,
      };
    }
  }

  return {
    ...defaultTime,
    timezone: settingObj.timezone || defaultTime.timezone,
    includeWeekends:
      settingObj.includeWeekends !== undefined
        ? settingObj.includeWeekends
        : defaultTime.includeWeekends,
  };
});
